import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "../lib/api";
import PhotoCarousel from "./PhotoCarousel";

/**
 * Looks up a carousel by slug and renders it inline. Used both by the
 * dedicated /gallery/:slug page and by the rich-text shortcode renderer
 * (`[[carousel:foo]]`) so a post body can drop a carousel anywhere.
 */
export default function CarouselEmbed({ slug, showHeader = false }) {
  const [carousel, setCarousel] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setCarousel(null);
    setError("");
    api
      .get(`/carousels/${encodeURIComponent(slug)}`)
      .then((r) => alive && setCarousel(r.data))
      .catch(() => alive && setError("not_found"));
    return () => { alive = false; };
  }, [slug]);

  if (error) {
    return (
      <div
        className="my-6 rounded-md border border-dashed border-black/15 p-6 text-center text-sm text-[var(--brand-body)]"
        data-testid={`carousel-embed-${slug}-missing`}
      >
        Carousel "{slug}" isn't available.
      </div>
    );
  }
  if (!carousel) {
    return (
      <div
        className="my-6 flex h-32 items-center justify-center gap-2 text-sm text-[var(--brand-body)]"
        data-testid={`carousel-embed-${slug}-loading`}
      >
        <Loader2 size={14} className="animate-spin" /> Loading carousel…
      </div>
    );
  }

  return (
    <div className="my-8" data-testid={`carousel-embed-${slug}`}>
      {showHeader && (carousel.kicker || carousel.name || carousel.intro) ? (
        <div className="mb-6 text-center">
          {carousel.kicker ? (
            <p className="label-kicker text-brand-accent">{carousel.kicker}</p>
          ) : null}
          {carousel.name ? (
            <h2 className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--brand-ink)] sm:text-4xl">
              {carousel.name}
            </h2>
          ) : null}
          {carousel.intro ? (
            <p className="mx-auto mt-3 max-w-2xl text-sm text-[var(--brand-body)]">
              {carousel.intro}
            </p>
          ) : null}
        </div>
      ) : null}
      <PhotoCarousel
        images={carousel.images || []}
        dataTestId={`carousel-${carousel.slug}`}
      />
    </div>
  );
}
